import { Box, CircularProgress, Typography } from "@mui/material"
import { t } from "i18next"
import React, { useContext, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useJobContext } from "../../contexts/JobContext"
import ShiftContext from "../../contexts/ShiftContext"
import SiteContext from "../../contexts/SiteContext"
import {
    clearSessionStorage,
    getCompletedJobGroups,
    jobInDeliveryPhase,
    setCompletedJobGroups,
    useUpdateStatusHook,
    useBulkUpdateStatusHook,
} from "../../entities/job/helper"
import { EJobStatus, IJob, stageTypes } from "../../entities/job/interface"
import { checkIfJobIsGrouped, getNextStops, getNextJobPart } from "../../utils/job"
import Proof from "./Proof"

interface IJobLoadedProps {
    jobStatus: EJobStatus
    stage: string
    handleBackNav: (isShow: boolean) => void
}

//to be put in job component
const Completed: React.FC<IJobLoadedProps> = ({ jobStatus, stage, handleBackNav }) => {
    const [showProof, setShowProof] = useState(false)
    const { currentJobs, setCurrentJobStatus, startNewJobPart, groupId, currentDestinationIndex, setIsConsecutive } =
        useJobContext()
    const navigate = useNavigate()
    const { handleJobUpdate, jobUpdateMutationLoading } = useUpdateStatusHook()
    const { handleBulkJobsUpdate, loading } = useBulkUpdateStatusHook()
    const siteContext = useContext(SiteContext)
    const context = useContext(ShiftContext)
    const { setIndexes } = useJobContext()

    // Handle context missing
    if (!context || !siteContext) {
        throw new Error("ShiftContext is not provided")
    }

    const { stops: loadedJobs, setStops: setLoadedJobs, setCompletedJobs } = context

    const handleJobCompletion = async (jobs: IJob[]) => {
        try {
            if (!loadedJobs || !groupId) return

            const isGroupJob = checkIfJobIsGrouped(loadedJobs, jobs[0])
            const lastJob = jobs[jobs.length - 1]

            // Update jobs if they are not completed
            if (!isGroupJob || stage === stageTypes.DELIVERY) {
                const jobIds = jobs.filter((job) => !job.isJobCompleted).map((job) => job._id)
                if (jobIds.length === 0) return

                // Update all jobs to the next status
                handleBulkJobsUpdate(jobIds, jobStatus)

                // Set the status in the context
                setCurrentJobStatus(jobStatus)
            }

            const nextStops = getNextStops(loadedJobs, groupId, currentDestinationIndex)
            if (nextStops && nextStops?.length > 1) {
                const nextJob = getNextJobPart(loadedJobs, setIndexes, lastJob, stage)
                window.sessionStorage.setItem("screens", JSON.stringify({ consecutive: "true", roadMap: "false" }))
                return nextJob && setIsConsecutive(true)
            }

            jobs.map((job: IJob) => {
                if (isGroupJob && stage === "delivery" && !job.isJobCompleted) {
                    const completedJob: IJob = { ...job, jobStatus: EJobStatus.completed, isJobCompleted: true }
                    setCompletedJobs((prev) => ({ ...prev, [job._id]: completedJob }))

                    try {
                        const completedJobGroups = getCompletedJobGroups()
                        if (groupId) {
                            const completedJobIds = completedJobGroups[groupId] || []
                            if (!completedJobIds.includes(job._id)) {
                                completedJobIds.push(job._id)
                                completedJobGroups[groupId] = completedJobIds
                                setCompletedJobGroups(completedJobGroups)
                            }
                        } else {
                            console.error("Group ID is null, cannot store completed job IDs")
                        }
                    } catch (error) {
                        console.error("Error saving completed job ID to localStorage:", error)
                    }
                }
            })

            // Handle next job transition
            const nextJob = getNextJobPart(loadedJobs, setIndexes, lastJob, stage)
            if (nextJob) {
                let nextStatus = jobStatus
                if (nextJob.type === "pickup" && nextJob.status !== EJobStatus.completed) {
                    nextStatus = EJobStatus.onRouteToPickup
                    await handleJobUpdate({ jobStatus: nextStatus }, nextJob.jobId)
                } else {
                    if (!jobInDeliveryPhase(nextJob.status) && nextJob.status !== EJobStatus.completed) {
                        nextStatus = EJobStatus.onRouteToDelivery
                        await handleJobUpdate({ jobStatus: nextStatus }, nextJob.jobId)
                    }
                }
                startNewJobPart(nextJob.jobId, nextJob.type)
            } else {
                const updatedJobs = { ...loadedJobs } // Create a shallow copy of loadedJobs
                delete updatedJobs[lastJob._id] // Remove the key corresponding to jobId
                setLoadedJobs(updatedJobs) // Update the loadedJobs context

                try {
                    const completedJobGroups = getCompletedJobGroups()
                    jobs.map((job: IJob) => {
                        if (job.groupId && completedJobGroups[job.groupId]) {
                            const completedJobIds = completedJobGroups[job.groupId]
                            delete completedJobGroups[job.groupId]

                            setCompletedJobs((prevCompletedJobs) => {
                                const updatedCompletedJobs = { ...prevCompletedJobs }
                                completedJobIds.forEach((jobId: string) => {
                                    delete updatedCompletedJobs[jobId]
                                })
                                return updatedCompletedJobs
                            })
                        } else if (!job.groupId) {
                            console.error("Group ID is null, cannot process completed job IDs")
                        }
                    })
                    setCompletedJobGroups(completedJobGroups)
                } catch (error) {
                    console.error("Error updating completed job groups in localStorage:", error)
                }
                clearSessionStorage([
                    "destinationStops",
                    "currentDestinationIndex",
                    "currentStopIndex",
                    "groupId",
                    "completedJobGroups",
                    "screens",
                ])
                navigate("/job-listing")
            }
        } catch (error) {
            // Handle error: open modal with error message
            console.error(error)
        }
    }

    const { site } = siteContext

    const handleProof = async (jobs: IJob[]) => {
        //Check if proof is enabled on Site level for now
        const captures = site?.settings?.notifications?.captures //Need to fetch these along with the job for address level

        if (captures) {
            const isEnabled =
                (stage === stageTypes.PICKUP && captures.pop?.enabled) ||
                (stage === stageTypes.DELIVERY && captures.pod?.enabled)

            if (isEnabled) {
                setShowProof(true)
                // handleBackNav(false)
            } else {
                return await handleJobCompletion(jobs)
            }
        } else {
            await handleJobCompletion(jobs)
        }
    }

    return (
        <>
            {showProof ? (
                <Proof stage={stage} handleJobCompletion={handleJobCompletion} handleBackNav={handleBackNav} />
            ) : (
                <Box
                    position={"absolute"}
                    width={"90vw"}
                    maxWidth={700}
                    display={"flex"}
                    alignItems={"center"}
                    bgcolor={"primary.main"}
                    alignSelf={"center"}
                    flexDirection={"column"}
                    borderRadius={6}
                    padding={"2%"}
                    paddingY={3}
                    textAlign={"center"}
                    bottom={"11vh"}
                    onClick={() => currentJobs && handleProof(currentJobs)}
                    marginBottom={6}
                >
                    {jobUpdateMutationLoading || loading ? (
                        <CircularProgress color="secondary" /> // Show loader when mutation is loading
                    ) : stage === stageTypes.PICKUP ? (
                        <>
                            <Typography variant="h5" color="#fff">
                                {t("jobs.mobile.completed.loadedTitle")}
                            </Typography>
                            <Typography variant="h6" color="#fff">
                                {t("jobs.mobile.completed.subTitle")}
                            </Typography>
                        </>
                    ) : (
                        <>
                            <Typography variant="h5" color="#fff">
                                {t("jobs.mobile.completed.unLoadedTitle")}
                            </Typography>
                            <Typography variant="h6" color="#fff">
                                {t("jobs.mobile.completed.subTitle")}
                            </Typography>
                        </>
                    )}
                </Box>
            )}
        </>
    )
}

export default Completed
